import { useEffect, useState } from "react";
import { useRecoilValue } from "recoil";
import { toast } from "react-hot-toast";
import { AiOutlineClose, AiOutlineCopy, AiOutlineMail } from "react-icons/ai";
import { SiGmail } from "react-icons/si";
import { PiMicrosoftOutlookLogo } from "react-icons/pi";
import { copyInvitationMessage, openEmailInvitation } from "../../../utils/test-invitation-link";
import { userAtom } from "../../../services/atoms/auth.atoms";
import CollaboratorsAPI from "../../../services/api/collaborators.api";

// "Enviar invitación" y "Enviar recordatorio": la plataforma manda
// el correo desde la casilla real de quien está en el panel (Gmail), con la plantilla de
// GrowMe y el link del test. Un correo de una casilla conocida pasa los filtros de
// empresa que retienen los automáticos (Microsoft 365 de Salmones Austral, oct-2026).
// Si su correo no está habilitado, o el envío falla, se ofrece abrir su correo con el
// mensaje ya escrito.
const TEXTS = {
  invitation: { title: 'Enviar el test desde tu correo', what: 'la invitación al test', sent: 'Test enviado' },
  reminder: { title: 'Enviar recordatorio desde tu correo', what: 'un recordatorio de su test pendiente', sent: 'Recordatorio enviado' },
};

const SendFromMyEmailModal = ({ target, onClose, onSent }) => {
  const user = useRecoilValue(userAtom);
  const [step, setStep] = useState('confirm'); // confirm | sending | manual
  const [notice, setNotice] = useState('');

  useEffect(() => {
    setStep('confirm');
    setNotice('');
  }, [target]);

  useEffect(() => {
    if (!target) return undefined;
    const onKey = (event) => { if (event.key === 'Escape' && step !== 'sending') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [target, onClose, step]);

  if (!target) return null;

  const kind = target.kind === 'reminder' ? 'reminder' : 'invitation';
  const texts = TEXTS[kind];
  const senderEmail = user?.data?.email;
  const who = target.name || 'la persona';

  const showManual = (message) => {
    setNotice(message);
    setStep('manual');
  };

  const send = async () => {
    setStep('sending');
    try {
      const { data } = await CollaboratorsAPI.sendFromMailbox({ collaboratorTestIDs: [target.collaboratorTestId], kind });
      const result = data?.results?.[0];
      if (result?.status === 'sent') {
        toast.success(`${texts.sent} a ${who} desde ${data.from}`);
        if (onSent) onSent();
        onClose();
        return;
      }
      if (result?.reason === 'finished') {
        toast('Esta persona ya respondió su test.', { icon: '✅' });
        onClose();
        return;
      }
      showManual('No se pudo enviar automáticamente. Envíalo tú desde tu correo, el mensaje ya viene escrito:');
    } catch (error) {
      showManual(error?.response?.data?.code === 'MAILBOX_NOT_ENABLED'
        ? 'Tu correo aún no está habilitado para enviar desde la plataforma. Envíalo tú desde tu correo, el mensaje ya viene escrito:'
        : 'No se pudo enviar automáticamente. Envíalo tú desde tu correo, el mensaje ya viene escrito:');
    }
  };

  const open = (provider) => {
    openEmailInvitation({ provider, ...target, kind });
    onClose();
  };

  return (
    <div className="gm-my-email-overlay" onClick={() => step !== 'sending' && onClose()}>
      <div className="gm-my-email-modal" role="dialog" aria-label={texts.title} onClick={(event) => event.stopPropagation()}>
        <div className="gm-my-email-header">
          <strong>{texts.title}</strong>
          <button type="button" title="Cerrar" disabled={step === 'sending'} onClick={onClose}><AiOutlineClose size={18} /></button>
        </div>

        {step !== 'manual' ? (
          <>
            <p className="gm-my-email-help">
              Se enviará {texts.what} a <strong>{who}</strong>{target.email ? <> ({target.email})</> : null} desde{' '}
              <strong>{senderEmail || 'tu correo'}</strong>, con el diseño de GrowMe y el link de su test.
              Llega como un correo tuyo, así no lo retienen los filtros de su empresa, y si responde te llega a ti.
            </p>
            <div className="gm-my-email-actions">
              <button type="button" className="gm-my-email-send" disabled={step === 'sending'} onClick={send}>
                <AiOutlineMail size={18} /> {step === 'sending' ? 'Enviando…' : 'Enviar ahora'}
              </button>
              <button type="button" className="gm-my-email-link" disabled={step === 'sending'} onClick={() => showManual('')}>
                Prefiero escribirlo yo
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="gm-my-email-help">
              {notice || <>Se abrirá tu correo con el mensaje y el link listos para <strong>{who}</strong>{target.email ? <> ({target.email})</> : null}. Solo tienes que apretar <strong>Enviar</strong>.</>}
            </p>
            <div className="gm-my-email-options">
              <button type="button" onClick={() => open('gmail')}><SiGmail size={18} /> Gmail</button>
              <button type="button" onClick={() => open('outlook')}><PiMicrosoftOutlookLogo size={18} /> Outlook</button>
              <button type="button" onClick={() => open('app')}><AiOutlineMail size={18} /> Otra app de correo</button>
              <button type="button" onClick={() => { copyInvitationMessage({ ...target, kind }); onClose(); }}><AiOutlineCopy size={18} /> Copiar mensaje</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SendFromMyEmailModal;
